import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, useEffect, useState } from "react";
import { InvoicePicker } from "@/components/Pickers";
import { DocumentLineRow } from "@/features/document-lines/DocumentLineRow";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import {
  toCreditNoteLinePayload,
  toMinor,
} from "@/features/document-lines/productDetails";
import { useOrganization } from "@/lib/organization";
import { createCreditNote } from "@/lib/api";

const CREDIT_NOTE_DRAFT_PREFIX = "niche-accounting.credit-note-draft.v1";

type CreatedCreditNote = {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  invoice_id: string;
  status: string;
};

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function CreditNoteCreateDrawer({
  open,
  initialInvoiceId,
  onOpenChange,
  onCreated,
  closeLabel = "Back to credits",
}: {
  open: boolean;
  initialInvoiceId?: string;
  onOpenChange: (open: boolean) => void;
  onCreated: (created: CreatedCreditNote) => void;
  closeLabel?: string;
}) {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";

  // `crmContactId` carries the source invoice id: a credit note is always
  // attached to one invoice, and the generic draft has no source field.
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(
      loadDocumentDraft(CREDIT_NOTE_DRAFT_PREFIX, organizationId) ??
        newDocumentDraft(),
    );
    setLoadedDraftKey(organizationId);
  }, [organizationId]);

  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(CREDIT_NOTE_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);

  useEffect(() => {
    if (!open || !initialInvoiceId || loadedDraftKey !== organizationId) return;
    setError(null);
    setDraft((current) =>
      current.crmContactId === initialInvoiceId
        ? current
        : { ...current, crmContactId: initialInvoiceId },
    );
  }, [initialInvoiceId, loadedDraftKey, open, organizationId]);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateLine = (
    index: number,
    patch: Partial<DocumentDraft["lines"][number]>,
  ) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const previewMinor = draft.lines.reduce(
    (sum, line) => sum + (toMinor(line.amount) ?? 0),
    0,
  );

  const clearDraft = () => {
    setDraft(newDocumentDraft());
    clearDocumentDraft(CREDIT_NOTE_DRAFT_PREFIX, organizationId);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const invoiceId = draft.crmContactId;
      if (!invoiceId)
        throw new Error("Pick the invoice this credit relates to.");
      const name = draft.partyName.trim();
      if (!name) throw new Error("Customer name is required.");
      if (!draft.issueDate) throw new Error("Issue date is required.");
      const created = await createCreditNote({
        invoice_id: invoiceId,
        customer_name: name,
        issue_date: draft.issueDate,
        lines: toCreditNoteLinePayload(draft.lines),
      });
      clearDraft();
      onOpenChange(false);
      onCreated(created);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="New credit note"
      description="Drafted against one invoice — nothing posts until the note is marked sent and applied. Closing this panel keeps your draft in this browser session."
      className="w-[min(46rem,100%)]"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            {closeLabel}
          </Button>
          {draft.partyName ||
          draft.crmContactId ||
          draft.lines.some((line) => line.description || line.amount) ? (
            <Button type="button" variant="ghost" onClick={clearDraft}>
              Discard draft
            </Button>
          ) : null}
          <Button
            type="submit"
            form="credit-note-create-form"
            disabled={submitting}
            className="ms-auto"
          >
            {submitting ? "Drafting…" : "Create credit note"}
          </Button>
        </>
      }
    >
      <form
        id="credit-note-create-form"
        onSubmit={submit}
        className="grid gap-5"
        aria-label="Create credit note"
      >
        {error ? (
          <div
            role="alert"
            className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
          >
            {error}
          </div>
        ) : null}
        <label htmlFor="cn-invoice" className="grid gap-1 text-xs font-medium">
          Invoice
          <InvoicePicker
            id="cn-invoice"
            value={draft.crmContactId}
            onChange={(invoiceId) => updateDraft({ crmContactId: invoiceId })}
            disabled={submitting}
            required
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label htmlFor="cn-customer" className="grid gap-1 text-xs font-medium">
            Customer name
            <Input
              id="cn-customer"
              value={draft.partyName}
              onChange={(e) => updateDraft({ partyName: e.target.value })}
              required
              placeholder="Acme Co."
              disabled={submitting}
            />
          </label>
          <label htmlFor="cn-date" className="grid gap-1 text-xs font-medium">
            Issue date
            <Input
              id="cn-date"
              type="date"
              value={draft.issueDate}
              onChange={(e) => updateDraft({ issueDate: e.target.value })}
              required
              disabled={submitting}
            />
          </label>
        </div>
        <fieldset className="grid gap-3">
          <legend className="text-xs font-medium">Lines</legend>
          {draft.lines.map((line, index) => (
            <DocumentLineRow
              key={line.uid}
              idPrefix={`cn-line-${index}`}
              index={index}
              line={line}
              onChange={(patch) => updateLine(index, patch)}
              onRemove={() =>
                setDraft((prev) => ({
                  ...prev,
                  lines:
                    prev.lines.length <= 1
                      ? prev.lines
                      : prev.lines.filter((_, i) => i !== index),
                }))
              }
              canRemove={draft.lines.length > 1}
              showTax={false}
              accountLabel="Revenue account"
              disabled={submitting}
            />
          ))}
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                setDraft((prev) => ({
                  ...prev,
                  lines: [...prev.lines, emptyDraftLine()],
                }))
              }
              disabled={submitting}
            >
              Add line
            </Button>
          </div>
        </fieldset>
        <p className="text-sm text-[var(--muted-foreground)]" aria-live="polite">
          Total:{" "}
          <span className="font-semibold text-[var(--foreground)]">
            {formatMoney({ cents: previewMinor, currency: "USD" })}
          </span>
        </p>
      </form>
    </Drawer>
  );
}
